// Package imports:
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp, faAngleDown } from "@fortawesome/pro-regular-svg-icons";
import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { getSearchTermsFromSearch } from "../../../services/utils";
import { useField } from "formik";

type SelectSize = "sm" | "lg" | "xs";

export interface IOptionProps {
    id?: string;
    label: string;
    value: string;
}

type SelectProps = React.InputHTMLAttributes<HTMLSelectElement>;

interface IOwnProps {
    label?: string;
    disabled?: boolean;
    options?: IOptionProps[];
    selectSize?: SelectSize;
    className?: string | React.HTMLAttributes<HTMLElement>;
    defaultValue?: number | string | string[] | null;
    onSelectChange?(s?: string | string[] | null): void;
    error?: string;
    usePortal?: boolean;
    children?: React.ReactNode;
    clearInput?: boolean;
}

type Props = IOwnProps & SelectProps;

const DropSelectSearch: React.FC<Props> = ({
    label,
    selectSize = "sm",
    disabled,
    options = [],
    className = "",
    defaultValue = "",
    onSelectChange = () => { },
    error,
    usePortal = false,
    children,
    clearInput = true,
    ...props
}) => {
    const [field, meta] = useField(props.name ?? '');
    const dropdown = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(defaultValue?.toString());
    const [search, setSearch] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isNavigating, setIsNavigating] = useState(false);
    const isMounted = useRef(false);

    const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);

    const hasValue = useMemo(() => {
        return (
            (selectedValue && selectedValue.toString().length >= 1) ||
            (search && search.length >= 1)
        );
    }, [selectedValue, search]);

    // Calculate dropdown position for portal
    const updateDropdownPosition = () => {
        if (dropdown.current && searchFieldInputRef.current && usePortal) {
            const inputRect = searchFieldInputRef.current.getBoundingClientRect();
            const containerRect = dropdown.current.getBoundingClientRect();

            const newPosition = {
                top: inputRect.bottom,
                left: inputRect.left,
                width: containerRect.width,
            };

            setDropdownPosition(newPosition);
        }
    };

    const handleOutsideClick = (event: any) => {
        if (dropdown.current) {
            if (!dropdown.current.contains(event.target)) {
                if (usePortal) {
                    const portalOverlay = document.querySelector('.drop-select__overlay--portal');
                    if (portalOverlay && portalOverlay.contains(event.target)) {
                        return;
                    }
                }
                setIsOpen(false);
            }
        }
    };

    useEffect(() => {
        setSelectedValue(defaultValue?.toString());
    }, [defaultValue]);

    useEffect(() => {
        window.addEventListener("click", handleOutsideClick);

        if (usePortal) {
            window.addEventListener("scroll", updateDropdownPosition, true);
            window.addEventListener("resize", updateDropdownPosition);
        }

        return () => {
            window.removeEventListener("click", handleOutsideClick);
            if (usePortal) {
                window.removeEventListener("scroll", updateDropdownPosition, true);
                window.removeEventListener("resize", updateDropdownPosition);
            }
        };
    }, [usePortal]);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }

        setIsOpen(false);
        setSearch("");
        setHighlightedIndex(-1);
        setIsNavigating(false);
        onSelectChange(selectedValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValue]);

    useEffect(() => {
        if (isOpen && usePortal) {
            const positionAttempts = [0, 10, 50, 100];

            positionAttempts.forEach(delay => {
                setTimeout(() => {
                    updateDropdownPosition();
                }, delay);
            });
        }
    }, [isOpen, usePortal]);

    // Reset highlighted index when options change
    useEffect(() => {
        setHighlightedIndex(-1);
        setIsNavigating(false);
    }, [search]);

    const selectedOption = useMemo(() => {
        return options.find(
            (opt) =>
                selectedValue && opt.value.toString() === selectedValue.toString()
        );
    }, [selectedValue, options]);

    const currentOptions = useMemo(() => {
        return options.filter(({ label }) =>
            label.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, options]);

    // Find the index of the selected option in the current filtered options
    const selectedOptionIndex = useMemo(() => {
        if (!selectedValue) return -1;
        return currentOptions.findIndex(
            (opt) => opt.value.toString() === selectedValue.toString()
        );
    }, [selectedValue, currentOptions]);

    const searchFieldInputRef = useRef<HTMLInputElement | null>(null);
    const searchDataAnchorElementsRef = useRef<(HTMLButtonElement | null)[]>([]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);

        if (!isOpen && value.length > 0) {
            setIsOpen(true);
            if (usePortal) {
                requestAnimationFrame(() => {
                    updateDropdownPosition();
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, usePortal]);

    const handleInputBlur = useCallback(() => {
        blurTimeoutRef.current = setTimeout(() => {
            setSearch("");
            setIsOpen(false);
            setHighlightedIndex(-1);
            setIsNavigating(false);
        }, 150);
    }, []);

    const handleOptionClick = useCallback((optionValue: string) => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }

        setSelectedValue(optionValue);
    }, []);

    const handleInputClick = useCallback(() => {
        if (!isOpen) {
            setIsOpen(true);
            if (usePortal) {
                requestAnimationFrame(() => {
                    updateDropdownPosition();
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, usePortal]);

    // Enhanced keyboard navigation with better scrolling
    const scrollOptionIntoView = (index: number) => {
        const element = searchDataAnchorElementsRef.current[index];
        if (element) {
            // Use a more reliable scrolling method
            element.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            });

            // Alternative: If the above doesn't work, try manual scrolling
            const container = element.closest('.drop-select__inner');
            if (container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();

                if (elementRect.top < containerRect.top) {
                    container.scrollTop -= containerRect.top - elementRect.top;
                } else if (elementRect.bottom > containerRect.bottom) {
                    container.scrollTop += elementRect.bottom - containerRect.bottom;
                }
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            // Open dropdown if closed
            setIsOpen(true);

            // Start with the selected option if it exists in current options, otherwise start with first option
            const startingIndex = selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
            setHighlightedIndex(startingIndex);
            setIsNavigating(true); // Start navigation mode

            if (usePortal) {
                requestAnimationFrame(() => {
                    updateDropdownPosition();
                });
            }

            // Scroll to the starting position
            requestAnimationFrame(() => {
                scrollOptionIntoView(startingIndex);
            });

            e.preventDefault();
            return;
        }

        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setIsNavigating(true); // Enable navigation mode
                setHighlightedIndex(prev => {
                    // If no item is highlighted yet, start from selected option or first option
                    if (prev === -1) {
                        const startingIndex = selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
                        requestAnimationFrame(() => {
                            scrollOptionIntoView(startingIndex);
                        });
                        return startingIndex;
                    }

                    const nextIndex = prev < currentOptions.length - 1 ? prev + 1 : 0;
                    // Use requestAnimationFrame to ensure the state update has completed
                    requestAnimationFrame(() => {
                        scrollOptionIntoView(nextIndex);
                    });
                    return nextIndex;
                });
                break;

            case 'ArrowUp':
                e.preventDefault();
                setIsNavigating(true); // Enable navigation mode
                setHighlightedIndex(prev => {
                    // If no item is highlighted yet, start from selected option or last option
                    if (prev === -1) {
                        const startingIndex = selectedOptionIndex >= 0 ? selectedOptionIndex : currentOptions.length - 1;
                        requestAnimationFrame(() => {
                            scrollOptionIntoView(startingIndex);
                        });
                        return startingIndex;
                    }

                    const nextIndex = prev > 0 ? prev - 1 : currentOptions.length - 1;
                    // Use requestAnimationFrame to ensure the state update has completed
                    requestAnimationFrame(() => {
                        scrollOptionIntoView(nextIndex);
                    });
                    return nextIndex;
                });
                break;

            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < currentOptions.length) {
                    const selectedOption = currentOptions[highlightedIndex];
                    setSelectedValue(selectedOption.value);
                }
                break;

            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsNavigating(false);
                setSearch("");
                break;
        }
    };

    const highlightText = useCallback((title: string): React.ReactNode => {
        if (!search) return title;

        const searchTerms = getSearchTermsFromSearch(search);
        if (searchTerms.length === 0) return title;

        const escaped = searchTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const regex = new RegExp(`(${escaped.join('|')})`, 'gi');

        const parts = title.split(regex);
        if (parts.length <= 1) return title;

        return parts.map((part, i) =>
            regex.test(part)
                ? <mark key={i}>{part}</mark>
                : part
        );
    }, [search]);

    const renderOverlayContent = () => (
        <div className="drop-select__inner">
            {children ? (
                <div>{children}</div>
            ) : (
                currentOptions.length > 0 &&
                currentOptions.map((option, i) => (
                    <button
                        key={`${option.value}-${i}`}
                        type="button"
                        tabIndex={-1}
                        className={cx("drop-select__item", {
                            "is-selected":
                                !isNavigating &&
                                selectedValue &&
                                option.value.toString() === selectedValue.toString(),
                            "is-highlighted": i === highlightedIndex,
                        })}
                        onClick={() => handleOptionClick(option.value)}
                        onMouseEnter={() => setHighlightedIndex(i)}
                        ref={(ref) => {
                            searchDataAnchorElementsRef.current[i] = ref;
                        }}
                    >
                        {highlightText(option.label)}
                    </button>
                ))
            )}
        </div>
    );

    return (
        <>
            <div
                className={cx(
                    "KCL_drop-select drop-select--search",
                    className.toString(),
                    selectSize,
                    {
                        "is-open": isOpen,
                        "has-value": hasValue,
                        disabled: disabled,
                        "has-error": meta.error && meta.touched && !isOpen,
                        "drop-select--portal": usePortal,
                    }
                )}
                ref={dropdown}
            >
                {label && (
                    <span className="drop-select__label">
                        <span>{label}</span>
                    </span>
                )}

                <input
                    disabled={disabled}
                    className="drop-select__trigger"
                    type="text"
                    {...field}
                    value={search}
                    onClick={handleInputClick}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={selectedOption?.label}
                    autoComplete="off"
                    ref={searchFieldInputRef}
                />
                {clearInput && hasValue && !disabled && (
                    <i onClick={() => {
                        setSearch("");
                        setSelectedValue("");
                    }}
                        className="drop-select__clear-icon input-icon"
                    ><FontAwesomeIcon icon={faCircleXmark} /></i>
                )}
                <i onClick={toggleOpen} className="input-icon">
                    <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} />
                </i>

                {!usePortal && isOpen && (
                    <div className="drop-select__overlay">
                        {renderOverlayContent()}
                    </div>
                )}
            </div>

            {usePortal && isOpen && createPortal(
                <div
                    className={cx("drop-select__overlay drop-select__overlay--portal", selectSize)}
                    style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        zIndex: 9999,
                    }}
                >
                    {renderOverlayContent()}
                </div>,
                document.body
            )}

            {meta.error && meta.touched && <span className="KCL_drop-select-error-message">{meta.error}</span>}
        </>
    );
};

export default DropSelectSearch;